import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    ButtonLink,
    ButtonStyles,
    DefaultContentComponent,
    DefaultFooterComponent,
    Dropdown,
    Icon,
    Icons,
    Label,
    Popup,
    showAlert,
    SwitcherTab
} from '@silverfort/sf-components';
import { DAYS_OPTIONS, FREQUENCY_OPTIONS, PERIOD_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { allTimezones, useTimezoneSelect } from 'react-timezone-select';
import ModulesField from './Fields/ModulesField';
import ReportBase from './ReportBase';
import { useReportsDrawer } from '../ReportsDrawerContext';
import { RecurringReportConfig, ReportType } from '../types';
import { DEFAULT_RECURRING_REPORT_CONFIG } from '../consts';
import RecipientsList from './Fields/RecipientsList';
import { isEmailValid } from '../../utils';

const FREQUENCY_ITEMS_LIST = Object.values(FREQUENCY_OPTIONS).map((option) => ({
    text: option.label,
    value: option.value
}));

const DAYS_ITEMS_LIST = Object.values(DAYS_OPTIONS).map((option) => ({
    title: option.label,
    value: option.value
}));

const PERIOD_ITEMS_LIST = Object.values(PERIOD_OPTIONS).map((option) => ({
    title: option.label,
    value: option.value
}));

const RecurringRiskReport: FC = () => {
    const { getRecurringReportData, upsertRecurringReportData, availableModules } = useReportsDrawer();
    const [reportData, setReportData] = useState<RecurringReportConfig>({ ...DEFAULT_RECURRING_REPORT_CONFIG });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isResetPopupOpen, setIsResetPopupOpen] = useState<boolean>(false);
    const { options: timezoneOptions } = useTimezoneSelect({
        labelStyle: 'original',
        timezones: allTimezones
    });

    useEffect(() => {
        const getReportData = async () => {
            const reportConfigs = await getRecurringReportData(ReportType.RiskReport);
            if (reportConfigs.length) {
                setReportData(reportConfigs[0]);
            } else {
                setReportData(getDefaultReportData());
            }
            setIsLoading(false);
        };
        getReportData();
    }, []);

    const isValid = useMemo(() => {
        if (
            !reportData.frequency ||
            !reportData.assessment_period ||
            reportData.hour === null ||
            !reportData.timezone ||
            reportData.metadata.modules.length === 0
        )
            return false;
        if (reportData.frequency === FREQUENCY_OPTIONS.WEEKLY.value && !reportData.day_of_week) return false;
        if (reportData.frequency === FREQUENCY_OPTIONS.MONTHLY.value && !reportData.day_of_month) return false;
        if (reportData.recipients.length === 0) {
            return false;
        }
        const recipientsSet = new Set(reportData.recipients);
        if (recipientsSet.size !== reportData.recipients.length) {
            return false;
        }
        for (const email of reportData.recipients) {
            if (!isEmailValid(email)) {
                return false;
            }
        }
        return true;
    }, [reportData]);

    const getDefaultReportData = useCallback(
        (): RecurringReportConfig => ({
            ...DEFAULT_RECURRING_REPORT_CONFIG,
            metadata: {
                ...DEFAULT_RECURRING_REPORT_CONFIG.metadata,
                modules: availableModules.filter(({ disabled }) => !disabled).map(({ value }) => value) as string[]
            }
        }),
        [availableModules]
    );

    const onSave = async () => {
        const updatedConfig = await upsertRecurringReportData({ ...reportData, is_active: true });
        if (updatedConfig) {
            setReportData(updatedConfig);
            showAlert('Recurring settings were saved successfully', 'success');
        } else {
            showAlert('Failed to save recurring settings', 'error');
        }
    };

    const onReset = async () => {
        setIsResetPopupOpen(false);
        const updatedConfig = await upsertRecurringReportData({
            id: reportData.id,
            ...getDefaultReportData()
        });
        if (updatedConfig) {
            setReportData(updatedConfig);
            showAlert('Recurring settings were reset successfully', 'success');
        } else {
            showAlert('Failed to reset recurring settings', 'error');
        }
    };

    const updateReportData = <T extends keyof RecurringReportConfig>(
        key: T,
        value: RecurringReportConfig[T] | null | undefined
    ) => {
        setReportData((prevState) => ({ ...prevState, [key]: value }));
    };

    const footerContent = (
        <Button
            text="Save recurring settings"
            buttonStyle={ButtonStyles.Primary}
            onClick={onSave}
            disabled={!isValid}
        />
    );

    return (
        <>
            <ReportBase
                headerTitle="Set recurring reports"
                headerDescription="Setting the recurring settings for generating and downloading risk reports."
                footerContent={footerContent}>
                {isLoading ? (
                    <div className="h-full bg-sf-gray-50 rounded-lg flex items-center justify-center">
                        <Icon icon={Icons.Loader} className="animate-spin text-sf-gray-400" size={24} />
                    </div>
                ) : (
                    <>
                        <ModulesField
                            modules={reportData.metadata.modules}
                            setModules={(modules) =>
                                updateReportData('metadata', {
                                    ...reportData.metadata,
                                    modules
                                })
                            }
                        />
                        <div className="flex flex-col gap-2">
                            <Label text="Select frequency & time" />
                            <div className="flex flex-col p-4 gap-4 border border-sf-gray-100 rounded-lg">
                                <div className="flex flex-col gap-2">
                                    <Label text="Frequency" />
                                    <SwitcherTab
                                        items={FREQUENCY_ITEMS_LIST}
                                        onSelect={(item) => updateReportData('frequency', item)}
                                        selectedItem={reportData.frequency}
                                        tabClassName="w-full"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    {reportData.frequency === FREQUENCY_OPTIONS.WEEKLY.value && (
                                        <div className="flex flex-col gap-2 mb-2">
                                            <Label text="Day of Week" />
                                            <Dropdown
                                                variant="trigger"
                                                items={DAYS_ITEMS_LIST}
                                                size="large"
                                                className="w-full"
                                                text="Select Day"
                                                onChange={(value) =>
                                                    updateReportData(
                                                        'day_of_week',
                                                        Array.isArray(value) ? value[0] : value
                                                    )
                                                }
                                                selectedItems={reportData.day_of_week}
                                                fullMenuWidth
                                            />
                                        </div>
                                    )}
                                    {reportData.frequency === FREQUENCY_OPTIONS.MONTHLY.value && (
                                        <div className="flex flex-col gap-2 mb-2">
                                            <Label text="Day of Month" />
                                            <Dropdown
                                                variant="trigger"
                                                items={Array.from({ length: 31 }, (_, index) => ({
                                                    value: index + 1,
                                                    title: `${index + 1}`
                                                }))}
                                                size="large"
                                                className="w-full"
                                                text="Select Day"
                                                onChange={(value) =>
                                                    updateReportData(
                                                        'day_of_month',
                                                        Array.isArray(value) ? value[0] : value
                                                    )
                                                }
                                                selectedItems={reportData.day_of_month}
                                                fullMenuWidth
                                            />
                                        </div>
                                    )}
                                    <div className="flex flex-col gap-2">
                                        <Label text="Time" />
                                        <Dropdown
                                            variant="trigger"
                                            items={Array.from({ length: 24 }, (_, index) => ({
                                                value: index,
                                                title: `${index}:00`
                                            }))}
                                            text="Select Time"
                                            size="large"
                                            className="w-full"
                                            onChange={(value) =>
                                                updateReportData('hour', Array.isArray(value) ? value[0] : value)
                                            }
                                            selectedItems={reportData.hour}
                                            fullMenuWidth
                                        />
                                        <Dropdown
                                            variant="trigger"
                                            items={timezoneOptions.map((timezoneOption) => ({
                                                value: timezoneOption.value,
                                                title: timezoneOption.label
                                            }))}
                                            onChange={(value) => {
                                                updateReportData('timezone', Array.isArray(value) ? value[0] : value);
                                            }}
                                            selectedItems={reportData.timezone}
                                            searchable
                                            size="large"
                                            className="w-full"
                                            text="Select Timezone"
                                            fullMenuWidth
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <Label text="Assessment period" />
                                    <Dropdown
                                        variant="trigger"
                                        items={PERIOD_ITEMS_LIST}
                                        size="large"
                                        className="w-full"
                                        onChange={(value) =>
                                            updateReportData(
                                                'assessment_period',
                                                Array.isArray(value) ? value[0] : value
                                            )
                                        }
                                        selectedItems={reportData.assessment_period}
                                        fullMenuWidth
                                    />
                                </div>
                                <RecipientsList
                                    recipients={reportData.recipients}
                                    setRecipients={(recipients) => updateReportData('recipients', recipients)}
                                />
                            </div>
                            {reportData.is_active && (
                                <ButtonLink
                                    text="Reset recurring settings"
                                    className="text-13"
                                    onClick={() => setIsResetPopupOpen(true)}
                                />
                            )}
                        </div>
                    </>
                )}
            </ReportBase>
            <ResetPopup isOpen={isResetPopupOpen} onReset={onReset} setIsOpen={setIsResetPopupOpen} />
        </>
    );
};

interface ResetPopupProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    onReset: () => void;
}

const ResetPopup: FC<ResetPopupProps> = ({ isOpen, setIsOpen, onReset }) => {
    return (
        <Popup
            className="z-[5001]"
            open={isOpen}
            onClose={() => setIsOpen(false)}
            onApprove={onReset}
            title="Reset recurring settings"
            ContentComponent={DefaultContentComponent}
            FooterComponent={DefaultFooterComponent}
            ContentComponentProps={{
                text: 'Are you sure you want to reset the recurring settings? This will permanently remove all scheduled recurring email sends'
            }}
            FooterComponentProps={{
                primaryButtonProps: { text: 'Reset' },
                secondaryButtonProps: { text: 'Cancel' }
            }}
        />
    );
};

export default RecurringRiskReport;
